import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CaretDown, Check, FolderSimple, FolderOpen, MagnifyingGlass, X } from '@phosphor-icons/react';
import { elternPfad, gruppiereOrdner, pfadAnzeige, sucheOrdner, type OrdnerOption } from '../../../../lib/dokumente/ordnerAuswahl';

/**
 * #518: Zielordner-Auswahl in „Zur Ablage“. Klappt in der Karte auf (kein System-Dropdown):
 * Suchfeld oben, Hauptordner als farbig abgesetzte Überschrift (selbst wählbar), Unterordner eingerückt.
 * Mit Suchtext: flache Trefferliste mit übergeordnetem Pfad.
 */
export function OrdnerAuswahl({ optionen, wert, onWahl, platzhalter, disabled }: {
  optionen: readonly OrdnerOption[];
  wert: string;
  onWahl: (pfad: string) => void;
  platzhalter: string;
  disabled?: boolean;
}) {
  const [offen, setOffen] = useState(false);
  const [suche, setSuche] = useState('');
  const huelle = useRef<HTMLDivElement>(null);
  const gruppen = useMemo(() => gruppiereOrdner(optionen), [optionen]);
  const treffer = useMemo(() => (suche.trim() ? sucheOrdner(optionen, suche) : []), [optionen, suche]);
  const gewaehlt = optionen.find(o => o.pfad === wert);

  useEffect(() => {
    if (!offen) return;
    const aussen = (e: MouseEvent) => { if (!huelle.current?.contains(e.target as Node)) setOffen(false); };
    document.addEventListener('mousedown', aussen);
    return () => document.removeEventListener('mousedown', aussen);
  }, [offen]);

  const waehle = (pfad: string) => { onWahl(pfad); setOffen(false); setSuche(''); };

  const zeile = (o: OrdnerOption, opts: { haupt?: boolean; unter?: string } = {}) => {
    const aktiv = o.pfad === wert;
    return (
      <button key={o.pfad} type="button" role="option" aria-selected={aktiv} onClick={() => waehle(o.pfad)}
        className="immo-menue-zeile flex w-full min-w-0 items-center text-left cursor-pointer"
        style={{
          gap: 8, border: 'none', fontFamily: 'inherit', color: 'var(--ink)',
          padding: opts.haupt ? '9px 10px' : `7px 10px 7px ${12 + Math.max(0, o.tiefe - 1) * 16}px`,
          borderRadius: 10, background: aktiv ? 'var(--acc-soft)' : 'transparent',
        }}>
        {opts.haupt ? <FolderSimple size={15} weight="fill" color="var(--acc)" className="shrink-0" /> : <FolderOpen size={14} color="var(--ink3)" className="shrink-0" />}
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate" style={{ fontSize: opts.haupt ? 13.5 : 13, fontWeight: opts.haupt ? 800 : 600, color: opts.haupt ? 'var(--acc)' : 'var(--ink)' }}>{o.name}</span>
          {opts.unter && <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{opts.unter}</span>}
        </span>
        {aktiv && <Check size={14} weight="bold" color="var(--acc)" className="shrink-0" />}
      </button>
    );
  };

  return (
    <div ref={huelle} className="flex flex-col min-w-0" style={{ gap: 6 }}>
      <button type="button" disabled={disabled} onClick={() => setOffen(o => !o)} aria-expanded={offen} aria-haspopup="listbox"
        className="relative flex w-full min-w-0 items-center text-left cursor-pointer"
        style={{ height: 44, padding: '0 40px 0 14px', borderRadius: 14, border: 'none', fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
          background: 'var(--bg)', color: wert ? 'var(--ink)' : 'var(--ink3)', boxShadow: offen ? 'inset 0 0 0 2px var(--acc)' : 'inset 0 0 0 1px var(--line)', opacity: disabled ? 0.6 : 1 }}>
        <span className="truncate">{wert ? `${pfadAnzeige(wert)}${gewaehlt?.name.endsWith('(wird angelegt)') ? ' (wird angelegt)' : ''}` : platzhalter}</span>
        <CaretDown weight="bold" size={12} color="var(--ink3)" style={{ position: 'absolute', right: 14, transform: offen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
      </button>

      {offen && (
        <div className="flex flex-col min-w-0" style={{ borderRadius: 16, padding: 8, gap: 6, background: 'var(--bg)', boxShadow: '0 18px 40px -16px var(--shade), inset 0 0 0 1px var(--line)' }}>
          <label className="flex items-center" style={{ height: 38, borderRadius: 12, gap: 8, padding: '0 10px', background: 'var(--fill2)' }}>
            <MagnifyingGlass size={15} color="var(--ink3)" aria-hidden />
            <input autoFocus value={suche} onChange={e => setSuche(e.target.value)} placeholder="Ordner suchen …" aria-label="Ordner suchen"
              onKeyDown={e => {
                if (e.key === 'Escape') { e.stopPropagation(); setOffen(false); }
                if (e.key === 'Enter' && treffer[0]) { e.preventDefault(); waehle(treffer[0].pfad); }
              }}
              className="flex-1 min-w-0" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13.5, color: 'var(--ink)', fontFamily: 'inherit' }} />
            {suche && (
              <button type="button" aria-label="Suche leeren" onClick={() => setSuche('')} className="flex items-center justify-center cursor-pointer"
                style={{ width: 24, height: 24, borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--ink3)' }}><X size={12} weight="bold" /></button>
            )}
          </label>

          <div role="listbox" aria-label="Zielordner" className="immo-scroll flex flex-col overflow-y-auto" style={{ maxHeight: 340, gap: 6 }}>
            {suche.trim() ? (
              treffer.length ? treffer.map(o => zeile(o, { haupt: o.tiefe === 0, unter: elternPfad(o.pfad) || undefined }))
                : <span style={{ padding: '10px', fontSize: 12.5, color: 'var(--ink3)' }}>Kein Ordner gefunden.</span>
            ) : gruppen.map(g => (
              <div key={g.haupt.pfad} className="flex flex-col" style={{ borderRadius: 12, background: 'color-mix(in srgb, var(--acc) 7%, transparent)', boxShadow: 'inset 3px 0 0 var(--acc)', padding: 2 }}>
                {zeile(g.haupt, { haupt: true })}
                {g.unter.map(u => zeile(u))}
              </div>
            ))}
            {!suche.trim() && gruppen.length === 0 && <span style={{ padding: '10px', fontSize: 12.5, color: 'var(--ink3)' }}>{platzhalter}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
